<template>
  <SearchComboBox
    id="companySearchBox"
    ref="companySearchBox"
    name="company-search"
    :results="clientSearchResults"
    :initial-value="selectedCompanyName"
    type="text"
    label-name="Your employer's name"
    class="mb-3"
    :status="fieldStatus"
    :error-text="errorText"
    :minimum-characters="3"
    :allow-empty-results="allowEmptyResults"
    @selected="queryFragment"
    @changing-input="resetErrorState"
    @error-state-changed="onErrorStateChanged"
  />
</template>

<script lang="ts">
import SearchComboBox from '@/components/search_combo_box/SearchComboBox.vue';
import { CLIENT_SEARCH_QUERY, CORPORATE_CLIENT_QUERY } from '@/graphql/bh_benefits/queries';
import helperStatus from '@/helpers/status';

export default {
  name: 'CorporateClientComboBox',
  components: {
    SearchComboBox,
  },
  props: {
    preselectedId: {
      type: String,
      default: '',
    },
    errorText: {
      type: String,
      default:
        'You may not be eligible for Premium through employer benefits, please <a href="/registration?member_type=seeker" class="underline">sign up here</a>',
    },
    status: {
      type: Number,
      default: helperStatus.Neutral,
    },
    allowEmptyResults: {
      type: Boolean,
      default: false,
    },
  },
  data(): any {
    return {
      selectedCompanyName: '',
      clientSearchResults: [],
      internalStatus: helperStatus.Neutral,
      selectionAttempted: false,
    };
  },
  computed: {
    fieldStatus(): number {
      if (this.status === helperStatus.Error || this.internalStatus === helperStatus.Error) {
        return helperStatus.Error;
      }
      return helperStatus.Neutral;
    },
  },
  mounted() {
    if (this.preselectedId) {
      this.queryGetClientSlug(this.preselectedId);
    }
  },
  methods: {
    onErrorStateChanged(hasError) {
      if (hasError) {
        this.internalStatus = helperStatus.Error;
      }
    },
    resetErrorState(): void {
      this.internalStatus = helperStatus.Neutral;
      this.$emit('employerInputChanged');
    },
    async queryGetClientSlug(bhClientId: string, displayName?: string) {
      await this.$apollo
        .query({
          query: CORPORATE_CLIENT_QUERY,
          variables: {
            bhClientId: Number(bhClientId),
          },
        })
        .then(({ data }) => {
          const { slug, name } = data.corporateClient;
          // Prefer the dropdown label so the field doesn't jump to a different corp name.
          // Preselect (next step) has no displayName and uses corporateClient.name.
          const employerName = displayName || name;
          this.selectedCompanyName = employerName;
          this.$emit('selectedClient', { slug, name: employerName, bhClientId, selectionAttempted: true });
        })
        .catch((error) => {
          this.$emit('selectedClient', {
            slug: '',
            name: this.selectedCompanyName,
            bhClientId,
            selectionAttempted: true,
          });
          console.warn('GraphQL query error:', error);
        });
    },
    async queryFragment({ value, display }) {
      if (value) {
        // user selected client from dropdown
        this.selectionAttempted = true;
        this.internalStatus = helperStatus.Neutral;
        this.selectedCompanyName = display;
        this.queryGetClientSlug(value, display);
      } else {
        // user typing in fragment — don't update selectedCompanyName/initial-value while typing,
        // or SearchComboBox will keep rebinding and autocomplete can stall.
        this.selectionAttempted = false;
        this.internalStatus = helperStatus.Neutral;
        await this.$apollo
          .query({
            query: CLIENT_SEARCH_QUERY,
            variables: {
              fragment: display,
            },
          })
          .then(({ data }) => {
            const corporateClientsForComboBox = data.clientSearch.map(({ clientId, clientName }) => {
              return { display: clientName, value: `${clientId}` };
            });
            this.$emit('selectedClient', { slug: '', name: display, bhClientId: '', selectionAttempted: false });
            this.clientSearchResults = corporateClientsForComboBox;
          })
          .catch((error) => {
            console.error('GraphQL query error:', error);
          });
      }
    },
  },
};
</script>
